import type Phaser from "phaser";
import { DAY_GLOW, LIGHT_SCALE, MAX_LIGHTS_PER_VIEW, flareReach, flickerInto, wobbleAt } from "daedalus-core";

/**
 * The light layer (ADR-141 decisions D1, D2 and D5 to D7, and its amendment of 2026-10-31): one darkness texture at
 * `LIGHT_SCALE` of the camera's resolution, filled with the sky's tint at the fill alpha, cut by a soft stamp for every
 * light (a pool of image objects drawn with the texture's erase, only the lights drawn, in one batch), and drawn over
 * the world with a pool of warm glows above it at the normal blend, each tinted the light's colour: at night at
 * `WARM_ALPHA` of the fill alpha, so a lamp's pool reads warm and not as the ground merely undarkened, by day at `GLOW_ALPHA`. At full light with no tint the fill and the cuts are
 * skipped and only the faint day glow is drawn. The view knows no clock, moon or game: it takes the ambient, the tint and
 * the lights it is given each frame, each light's strength already holding its share for the sky (`lightOn`), and scales
 * each cut and glow by the light's flicker at the frame's time unless the frame says not to (ADR-141's amendment of
 * 2026-11-01, decision 4). Its own objects are all made in the constructor and none after it; the engine's draw
 * path still allocates a few hundred bytes a light each frame (P2.25-T17).
 */

/**
 * The floats per light in `LightingFrame.lights`: x, y, radius, red, green, blue (0 to 255), strength (0 to 1, the caller's
 * share for the sky already in it, `lightOn`), the flicker's amount and rate in hertz, and the light's seed (ADR-141's
 * amendment of 2026-11-01, decision 4), then how wild the flicker is (0 to 1) and how many pixels its glow wobbles
 * (P2.25-T29).
 */
export const LIGHT_STRIDE = 12;

/**
 * How often a light's wobble is worked out again, in milliseconds: the drift is whole pixels at a slow rate, so it moves
 * no more often than this.
 */
const WOBBLE_STEP_MS = 100;

/**
 * The numbers kept per cutter and per glow of where it was last put.
 */
const PLACED_FIELDS = 3;

/**
 * The depth of the darkness in its layer: over every prop and character, which sort by row below it.
 */
export const DARKNESS_DEPTH = 1_000_000_000;

/**
 * The depth of the warm glows, just over the darkness; emissive things draw over both.
 */
export const GLOW_DEPTH = DARKNESS_DEPTH + 1;

/**
 * The alpha of a light's glow by day, at full strength: a faint warm tint around the light (decision D5).
 */
export const GLOW_ALPHA = 0.12;

/**
 * How strongly a light's glow tints its pool at night, per unit of the darkness's fill alpha (the glow's alpha is this
 * times the fill alpha times the light's share, held to 1), so a light adds its colour as well as taking the night away.
 * The glow is drawn at the normal blend, so it can only tint toward the colour and never blows out to white or flat
 * yellow.
 */
export const WARM_ALPHA = 1.3;

/**
 * Phaser's smooth texture filter (`Phaser.Textures.FilterMode.LINEAR`).
 */
const FILTER_LINEAR = 0;

const STAMP_SIZE = 128;
const STAMP_KEY = "daedalus:light-stamp";
/**
 * The creation config of a cutter image: the stamp texture, never added to the scene's display list (the engine reads `key` from it, which its type does not list).
 */
const CUTTER_CONFIG = { x: 0, y: 0, key: STAMP_KEY, add: false } as Phaser.Types.GameObjects.GameObjectConfig;
const DARKNESS_KEY = "daedalus:darkness";

/**
 * One piece of art that eases the darkness where it is drawn: a frame of a texture laid at a world place, as a prop view lays it. The
 * darkness is erased by the frame's own pixels at `LightingFrame.easeShare`, so an open roof's building is lit in its own outline.
 */
export interface EaseStamp {
  /** The atlas page's texture key and the frame's name in it. */
  readonly key: string;
  readonly frame: string;
  /** The frame's bottom-left in world pixels, where a prop's anchor puts it. */
  readonly x: number;
  readonly y: number;
  readonly flipX: boolean;
  readonly flipY: boolean;
}

/**
 * No stamps, shared by every frame and every caller that has none.
 */
export const NO_STAMPS: readonly EaseStamp[] = [];

/**
 * No images, shared by every frame that has none to erase with.
 */
const NO_IMAGES: Phaser.GameObjects.Image[] = [];

/**
 * What the view is told each frame.
 */
export interface LightingFrame {
  /** The world rectangle the camera shows, in world pixels. */
  left: number;
  top: number;
  width: number;
  height: number;
  /** The camera's resolution in device pixels. */
  deviceWidth: number;
  deviceHeight: number;
  /** The sky: ambient 0 to 1, the tint colour 0 to 255 and the fill's alpha. */
  ambient: number;
  r: number;
  g: number;
  b: number;
  tint: number;
  /** The ambient the dark holds tonight, which a light's strength follows (D5). */
  nightLevel: number;
  /** The server's time in milliseconds, which the flicker follows. */
  time: number;
  /** Whether lights flicker; false under Reduced motion. */
  flicker: boolean;
  /** The lights, `LIGHT_STRIDE` floats each, `lightCount` of them. */
  lights: Float32Array;
  lightCount: number;
  /** The art that eases the darkness where it is drawn, and the share of the darkness each spares, 0 to 1. The list is replaced, never edited, when it changes. */
  easeStamps: readonly EaseStamp[];
  easeShare: number;
}

/**
 * What the last frame drew, for the tests and the report.
 */
export interface LightingStats {
  /** Frames that filled the darkness. */
  fills: number;
  /** Cuts made in the darkness, lights. */
  cuts: number;
  /** Glows shown in the last frame. */
  glows: number;
}

/**
 * Makes a frame at full light with no lights, for a caller that keeps one and rewrites it.
 *
 * @returns The frame.
 */
export function newLightingFrame(): LightingFrame {
  return {
    left: 0, top: 0, width: 1, height: 1, deviceWidth: 1, deviceHeight: 1,
    ambient: 1, r: 255, g: 255, b: 255, tint: 0, nightLevel: 0.5, time: 0, flicker: true,
    lights: new Float32Array(MAX_LIGHTS_PER_VIEW * LIGHT_STRIDE), lightCount: 0,
    easeStamps: NO_STAMPS, easeShare: 0
  };
}

/**
 * The strength of a light for a sky: full in the dark, easing up through dusk, never below `DAY_GLOW` (D5).
 *
 * @param ambient The sky's ambient.
 * @param nightLevel The ambient the dark holds.
 * @returns 0 to 1, at least `DAY_GLOW`.
 */
export function lightStrength(ambient: number, nightLevel: number): number {
  const span = 1 - nightLevel;
  const dark = span > 0 ? (1 - ambient) / span : 0;
  return Math.min(1, Math.max(DAY_GLOW, dark));
}

/**
 * The light layer of one scene.
 */
export class LightingView {
  public readonly stats: LightingStats = { fills: 0, cuts: 0, glows: 0 };
  private readonly darkness: Phaser.Textures.DynamicTexture;
  private readonly image: Phaser.GameObjects.Image;
  private readonly glows: Phaser.GameObjects.Image[] = [];
  private readonly cutters: Phaser.GameObjects.Image[] = [];
  /** The first n cutters for each n, made once, so a frame hands the texture just the lights it draws in one batch and allocates no array. */
  private readonly cutterRuns: Phaser.GameObjects.Image[][] = [];
  private cutting = 0;
  private readonly easers: Phaser.GameObjects.Image[] = [];
  private easeActive: Phaser.GameObjects.Image[] = [];
  private easeFrom: readonly EaseStamp[] = NO_STAMPS;
  private mixCount = -1;
  private mixEase: Phaser.GameObjects.Image[] = NO_IMAGES;
  private mix: Phaser.GameObjects.Image[] = NO_IMAGES;
  private readonly tints = new Int32Array(MAX_LIGHTS_PER_VIEW).fill(-1);
  private readonly shares = new Float32Array(MAX_LIGHTS_PER_VIEW);
  /** The flicker factor of the light being worked out, kept in double precision so the share is rounded to float32 once. */
  private readonly flick = new Float64Array(1);
  /**
   * Where each cutter and each glow was last put (x, y, then the cutter's scale or the glow's radius), so a frame that does
   * not move a light writes nothing to it: each write of a changed number to an engine object stores a boxed number.
   */
  private readonly cutPlaced = new Float64Array(MAX_LIGHTS_PER_VIEW * PLACED_FIELDS).fill(Number.NaN);
  private readonly glowPlaced = new Float64Array(MAX_LIGHTS_PER_VIEW * PLACED_FIELDS).fill(Number.NaN);
  /** Each light's wobble this frame, across and down in whole world pixels, so a pool moves a few times a second (each move costs the engine a redraw of its transform) and not every frame. */
  private readonly drift = new Float32Array(MAX_LIGHTS_PER_VIEW * 2);
  /** The seed each slot's drift was worked out for, and the wobble step it was worked out in: drift is worked out again only when the step moves on or a slot holds another light; a slot past the frame's lights is forgotten, so a light that comes back into it is worked out afresh. */
  private readonly driftSeeds = new Float64Array(MAX_LIGHTS_PER_VIEW).fill(-1);
  private wobbleStep = -2;

  /**
   * @param scene Scene to draw into; the view adds its objects through `scene.add`.
   * @throws Error when the darkness texture cannot be made, as on a canvas renderer.
   */
  public constructor(private readonly scene: Phaser.Scene) {
    if (!scene.textures.exists(STAMP_KEY)) {
      const canvas = scene.textures.createCanvas(STAMP_KEY, STAMP_SIZE, STAMP_SIZE);
      if (canvas === null) {
        throw new Error("the light stamp could not be made");
      }
      const context = canvas.getContext();
      const pixels = context.createImageData(STAMP_SIZE, STAMP_SIZE);
      const half = STAMP_SIZE / 2;
      for (let y = 0; y < STAMP_SIZE; y += 1) {
        for (let x = 0; x < STAMP_SIZE; x += 1) {
          const d = Math.hypot(x + 0.5 - half, y + 0.5 - half) / half;
          const f = d >= 1 ? 0 : 1 - d;
          const at = (y * STAMP_SIZE + x) * 4;
          pixels.data[at] = 255;
          pixels.data[at + 1] = 255;
          pixels.data[at + 2] = 255;
          pixels.data[at + 3] = Math.round(255 * f * f * (3 - 2 * f));
        }
      }
      context.putImageData(pixels, 0, 0);
      canvas.refresh();
    }
    const darkness = scene.textures.addDynamicTexture(DARKNESS_KEY, 2, 2);
    if (darkness === null) {
      throw new Error("the darkness texture could not be made");
    }
    darkness.setFilter(FILTER_LINEAR);
    this.darkness = darkness;
    this.image = scene.add.image(0, 0, DARKNESS_KEY).setOrigin(0, 0).setDepth(DARKNESS_DEPTH).setVisible(false);
    for (let index = 0; index < MAX_LIGHTS_PER_VIEW; index += 1) {
      this.cutters.push(scene.make.image(CUTTER_CONFIG, false).setAlpha(0));
      this.glows.push(scene.add.image(0, 0, STAMP_KEY).setDepth(GLOW_DEPTH).setVisible(false));
    }
    for (let count = 1; count <= MAX_LIGHTS_PER_VIEW; count += 1) {
      this.cutterRuns.push(this.cutters.slice(0, count));
    }
  }

  /**
   * Draws one frame: fills and cuts the darkness unless the sky is at full light with no tint, places the glows.
   *
   * @param frame What to draw.
   */
  public frame(frame: LightingFrame): void {
    const count = Math.min(frame.lightCount, MAX_LIGHTS_PER_VIEW);
    const dark = frame.tint > 0.001;
    const lights = frame.lights;
    const step = frame.flicker ? Math.floor(frame.time / WOBBLE_STEP_MS) : -1;
    for (let index = 0; index < count; index += 1) {
      const at = index * LIGHT_STRIDE;
      const seed = lights[at + 9] as number;
      const wobble = frame.flicker ? (lights[at + 11] as number) : 0;
      if (frame.flicker) {
        flickerInto(this.flick, 0, frame.time, seed, lights[at + 7] as number, lights[at + 8] as number, lights[at + 10] as number);
        this.shares[index] = (this.flick[0] as number) * (lights[at + 6] as number);
      } else {
        this.shares[index] = lights[at + 6] as number;
      }
      if (step !== this.wobbleStep || this.driftSeeds[index] !== seed) {
        this.driftSeeds[index] = seed;
        this.drift[index * 2] = wobble === 0 ? 0 : Math.round(wobble * wobbleAt(frame.time, seed, 0));
        this.drift[index * 2 + 1] = wobble === 0 ? 0 : Math.round(wobble * wobbleAt(frame.time, seed, 1));
      }
    }
    this.wobbleStep = step;
    for (let index = count; index < MAX_LIGHTS_PER_VIEW; index += 1) {
      this.driftSeeds[index] = -1;
    }
    this.stats.cuts = 0;
    if (dark) {
      this.fillAndCut(frame, count);
    } else {
      this.image.setVisible(false);
    }
    this.placeGlows(frame, count);
  }

  /**
   * Fills the darkness with the tint, cuts the lights out of it and shows it over the view.
   *
   * @param frame The frame.
   * @param count The lights drawn.
   */
  private fillAndCut(frame: LightingFrame, count: number): void {
    const width = Math.max(2, Math.round(frame.deviceWidth * LIGHT_SCALE));
    const height = Math.max(2, Math.round(frame.deviceHeight * LIGHT_SCALE));
    const texture = this.darkness;
    if (texture.width !== width || texture.height !== height) {
      texture.setSize(width, height, false);
      texture.setFilter(FILTER_LINEAR);
    }
    const kx = width / frame.width;
    const ky = height / frame.height;
    texture.clear();
    texture.fill((Math.round(frame.r) << 16) | (Math.round(frame.g) << 8) | Math.round(frame.b), frame.tint);
    const lights = frame.lights;
    for (let index = 0; index < count; index += 1) {
      const at = index * LIGHT_STRIDE;
      const radius = (lights[at + 2] as number) * flareReach(this.shares[index] as number);
      const cutter = this.cutters[index] as Phaser.GameObjects.Image;
      const x = (Math.round((lights[at] as number) + (this.drift[index * 2] as number)) - frame.left) * kx;
      const y = (Math.round((lights[at + 1] as number) + (this.drift[index * 2 + 1] as number)) - frame.top) * ky;
      const scale = (2 * radius * kx) / STAMP_SIZE;
      const seat = index * PLACED_FIELDS;
      if (x !== this.cutPlaced[seat] || y !== this.cutPlaced[seat + 1]) {
        cutter.setPosition(x, y);
        this.cutPlaced[seat] = x;
        this.cutPlaced[seat + 1] = y;
      }
      if (scale !== this.cutPlaced[seat + 2]) {
        cutter.setScale(scale);
        this.cutPlaced[seat + 2] = scale;
      }
      cutter.setAlpha(Math.min(1, this.shares[index] as number));
      this.stats.cuts += 1;
    }
    for (let index = count; index < this.cutting; index += 1) {
      (this.cutters[index] as Phaser.GameObjects.Image).setAlpha(0);
    }
    this.cutting = count;
    const run = this.runOf(count, this.placeEase(frame, kx, ky));
    if (run.length > 0) {
      texture.erase(run);
    }
    texture.render();
    this.stats.fills += 1;
    this.image.setPosition(frame.left, frame.top).setDisplaySize(frame.width, frame.height).setVisible(true);
  }

  /**
   * Places the images that ease the darkness by the stamps' art at the share they spare. The images are made and given their frames
   * when the list of stamps changes (a roof sheltering the character or not), and a frame only moves them.
   *
   * @param frame The frame.
   * @param kx The texture's pixels per world pixel across.
   * @param ky The same down.
   * @returns The images to erase with, the shared empty list when there are none.
   */
  private placeEase(frame: LightingFrame, kx: number, ky: number): Phaser.GameObjects.Image[] {
    const stamps = frame.easeStamps;
    if (stamps !== this.easeFrom) {
      this.easeFrom = stamps;
      this.seat(stamps);
    }
    if (this.easeActive.length === 0 || !(frame.easeShare > 0)) {
      return NO_IMAGES;
    }
    for (let index = 0; index < this.easeActive.length; index += 1) {
      const stamp = stamps[index] as EaseStamp;
      (this.easeActive[index] as Phaser.GameObjects.Image).setPosition((stamp.x - frame.left) * kx, (stamp.y - frame.top) * ky).setScale(kx).setAlpha(Math.min(1, frame.easeShare));
    }
    return this.easeActive;
  }

  /**
   * The images one erase pass draws: the lights' cutters, then the ease stamps' images. The list is made when the count of lights or
   * the stamps change and is the same object while they do not, so a frame allocates nothing and the darkness is erased in one batch.
   *
   * @param count The lights cut this frame.
   * @param eased The ease images this frame.
   * @returns The images, in the order they erase.
   */
  private runOf(count: number, eased: Phaser.GameObjects.Image[]): Phaser.GameObjects.Image[] {
    if (eased === NO_IMAGES) {
      return count === 0 ? NO_IMAGES : (this.cutterRuns[count - 1] as Phaser.GameObjects.Image[]);
    }
    if (this.mixCount !== count || this.mixEase !== eased) {
      this.mixCount = count;
      this.mixEase = eased;
      this.mix = [...(count === 0 ? NO_IMAGES : (this.cutterRuns[count - 1] as Phaser.GameObjects.Image[])), ...eased];
    }
    return this.mix;
  }

  /**
   * Gives each stamp an image showing its frame, growing the pool when it is short.
   *
   * @param stamps The stamps.
   */
  private seat(stamps: readonly EaseStamp[]): void {
    for (let index = this.easers.length; index < stamps.length; index += 1) {
      const stamp = stamps[index] as EaseStamp;
      this.easers.push(this.scene.make.image({ x: 0, y: 0, key: stamp.key, frame: stamp.frame, add: false } as Phaser.Types.GameObjects.GameObjectConfig, false).setOrigin(0, 1));
    }
    for (let index = 0; index < stamps.length; index += 1) {
      const stamp = stamps[index] as EaseStamp;
      (this.easers[index] as Phaser.GameObjects.Image).setTexture(stamp.key, stamp.frame).setFlip(stamp.flipX, stamp.flipY);
    }
    this.easeActive = this.easers.slice(0, stamps.length);
  }

  /**
   * Shows the warm glow of each light, at whole world pixels (a light stands on whole pixels, and a whole-number position is stored without a boxed number each frame), and hides the rest of the pool.
   *
   * @param frame The frame.
   * @param count The lights drawn.
   */
  private placeGlows(frame: LightingFrame, count: number): void {
    const lights = frame.lights;
    const dark = frame.tint > 0.001;
    for (let index = 0; index < this.glows.length; index += 1) {
      const glow = this.glows[index] as Phaser.GameObjects.Image;
      if (index >= count) {
        glow.setVisible(false);
        continue;
      }
      const at = index * LIGHT_STRIDE;
      const radius = (lights[at + 2] as number) * flareReach(this.shares[index] as number);
      const x = Math.round((lights[at] as number) + (this.drift[index * 2] as number));
      const y = Math.round((lights[at + 1] as number) + (this.drift[index * 2 + 1] as number));
      const seat = index * PLACED_FIELDS;
      if (x !== this.glowPlaced[seat] || y !== this.glowPlaced[seat + 1]) {
        glow.setPosition(x, y);
        this.glowPlaced[seat] = x;
        this.glowPlaced[seat + 1] = y;
      }
      if (radius !== this.glowPlaced[seat + 2]) {
        glow.setDisplaySize(2 * radius, 2 * radius);
        this.glowPlaced[seat + 2] = radius;
      }
      const tint = (Math.round(lights[at + 3] as number) << 16) | (Math.round(lights[at + 4] as number) << 8) | Math.round(lights[at + 5] as number);
      if (tint !== this.tints[index]) {
        glow.setTint(tint);
        this.tints[index] = tint;
      }
      glow.setAlpha(Math.min(1, (dark ? WARM_ALPHA * frame.tint : GLOW_ALPHA) * (this.shares[index] as number))).setVisible(true);
    }
    this.stats.glows = count;
  }

  /**
   * Removes the view's objects and textures.
   */
  public destroy(): void {
    this.image.destroy();
    for (const cutter of this.cutters) {
      cutter.destroy();
    }
    this.cutters.length = 0;
    for (const easer of this.easers) {
      easer.destroy();
    }
    this.easers.length = 0;
    this.easeActive = [];
    this.mix = NO_IMAGES;
    this.mixEase = NO_IMAGES;
    this.cutterRuns.length = 0;
    for (const glow of this.glows) {
      glow.destroy();
    }
    this.glows.length = 0;
    this.scene.textures.remove(DARKNESS_KEY);
  }
}
